import { useEffect, useMemo, useState } from "react";
import { useGame } from "@/context/GameContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Skull, Swords, Search, Globe2, Filter } from "lucide-react";

const TIER_META = {
  common:    { label: "Common",    color: "text-stone-300",  ring: "border-stone-700",       bar: "bg-stone-500"   },
  elite:     { label: "Elite",     color: "text-amber-300",  ring: "border-amber-800/60",    bar: "bg-amber-500"   },
  legendary: { label: "Legendary", color: "text-red-300",    ring: "border-red-800/60",      bar: "bg-red-500"     },
  raid:      { label: "Raid",      color: "text-violet-300", ring: "border-violet-700/60",   bar: "bg-violet-500"  },
};
const TIER_ORDER = ["common", "elite", "legendary", "raid"];

/**
 * World Bosses hub — the dedicated panel behind the Combat sidebar item.
 *
 * Loads the full 42-boss roster once, groups by tier for the filter bar,
 * lets the player filter by tier + free-text search across name/region/
 * country, and delegates the engagement to `useGame().startCombat` — same
 * pipeline as any other monster fight.
 */
export function WorldBossesPanel() {
  const { startCombat } = useGame();
  const [bosses, setBosses] = useState([]);
  const [query, setQuery]   = useState("");
  const [tier, setTier]     = useState("all");
  const [busy, setBusy]     = useState(false);

  useEffect(() => {
    api.get("/api/game/map/bosses").then(({ data }) => {
      setBosses(data.bosses || []);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bosses.filter((b) => {
      if (tier !== "all" && b.tier !== tier) return false;
      if (!q) return true;
      return (
        b.name.toLowerCase().includes(q) ||
        (b.region || "").toLowerCase().includes(q) ||
        (b.countries || []).some((c) => c.toLowerCase() === q)
      );
    });
  }, [bosses, query, tier]);

  const counts = useMemo(() => {
    const c = { all: bosses.length, common: 0, elite: 0, legendary: 0, raid: 0 };
    for (const b of bosses) if (c[b.tier] != null) c[b.tier]++;
    return c;
  }, [bosses]);

  async function engage(id, name) {
    if (busy) return;
    setBusy(true);
    try {
      await startCombat?.(id);
      toast.success(`Engaging ${name}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="world-bosses-panel">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-red-400 uppercase tracking-widest text-[10px] font-numeric">
            <Skull size={11} /> The Great Beasts
          </div>
          <h2 className="font-display text-3xl heading-gilded mt-0.5">World Bosses</h2>
          <p className="text-stone-500 font-body max-w-2xl">
            Forty-two apex creatures scattered across every continent. Track
            them, best them, and take home the trophies that etch your name
            into the reliquaries of Territoria.
          </p>
        </div>
        <div className="flex gap-4 text-[10px] font-numeric uppercase tracking-widest text-stone-500">
          {TIER_ORDER.map((t) => (
            <div key={t}>
              <div className={`${TIER_META[t].color} text-lg font-display leading-none`}>{counts[t]}</div>
              {TIER_META[t].label}
            </div>
          ))}
        </div>
      </header>

      {/* Filters */}
      <div className="panel p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-[#0b1220]/80 border border-stone-700 rounded px-2.5 py-1.5 min-w-[240px]">
          <Search size={12} className="text-stone-500 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search bosses, biome, ISO"
            className="bg-transparent text-sm text-stone-100 placeholder:text-stone-500 outline-none flex-1 font-body"
            data-testid="boss-search-input"
          />
        </div>
        <div className="flex items-center gap-1.5" data-testid="boss-tier-filter">
          <Filter size={11} className="text-stone-500" />
          <TierPill label="All"       count={counts.all}       active={tier === "all"}       onClick={() => setTier("all")}       tone="text-stone-200" />
          {TIER_ORDER.map((t) => (
            <TierPill
              key={t}
              label={TIER_META[t].label}
              count={counts[t]}
              active={tier === t}
              onClick={() => setTier(t)}
              tone={TIER_META[t].color}
              testId={`boss-tier-${t}`}
            />
          ))}
        </div>
        <div className="ml-auto text-[10px] font-numeric uppercase tracking-widest text-stone-500">
          {filtered.length} shown
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((b) => {
          const t = TIER_META[b.tier] || TIER_META.common;
          return (
            <article
              key={b.id}
              className={`panel p-3 flex flex-col gap-2 border ${t.ring}`}
              data-testid={`boss-${b.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full grid place-items-center bg-[#0b0906] border border-stone-800 shrink-0">
                  <Skull size={14} className={t.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="uppercase tracking-widest text-[10px] font-numeric flex items-center gap-2">
                    <span className={t.color}>{t.label}</span>
                    <span className="text-stone-600">·</span>
                    <span className="text-red-400">CB {b.combat_level}</span>
                  </div>
                  <h3 className="font-display text-lg heading-gilded leading-tight truncate">
                    {b.name}
                  </h3>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[10px] font-numeric uppercase tracking-wider text-stone-500">
                <Globe2 size={10} />
                <span className="truncate">{b.region}</span>
              </div>

              {(b.countries || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {b.countries.slice(0, 8).map((iso) => (
                    <span
                      key={iso}
                      className="text-[10px] font-numeric text-stone-400 border border-stone-800 rounded px-1.5 py-0.5"
                    >
                      {iso}
                    </span>
                  ))}
                  {b.countries.length > 8 && (
                    <span className="text-[10px] font-numeric text-stone-500">
                      +{b.countries.length - 8}
                    </span>
                  )}
                </div>
              )}

              <p className="text-[11px] font-body italic text-stone-500 leading-snug">
                {b.flavor}
              </p>

              <button
                disabled={busy}
                onClick={() => engage(b.id, b.name)}
                className="mt-auto flex items-center justify-center gap-1.5 text-[11px] font-numeric uppercase tracking-wider rounded border border-red-800/70 text-red-300 hover:bg-red-900/25 hover:border-red-500 py-1.5 disabled:opacity-50 transition-colors"
                data-testid={`boss-engage-${b.id}`}
              >
                <Swords size={11} /> Engage
              </button>
            </article>
          );
        })}
        {filtered.length === 0 && bosses.length > 0 && (
          <div className="col-span-full text-center text-stone-500 font-body italic py-10">
            No beasts match those filters.
          </div>
        )}
      </div>
    </div>
  );
}

function TierPill({ label, count, active, onClick, tone, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={
        "text-[10px] font-numeric uppercase tracking-widest px-2 py-1 rounded border transition-colors " +
        (active
          ? `${tone} border-amber-600/70 bg-amber-900/15`
          : "text-stone-400 border-stone-800 hover:border-stone-600")
      }
    >
      {label} <span className="text-stone-500">· {count}</span>
    </button>
  );
}
